// Generadores de reportes PDF / Excel para los distintos módulos.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx-js-style";
import { DEJAVU_REGULAR_BASE64, DEJAVU_BOLD_BASE64 } from "./boletaFont";

const colones = (n) => {
  const v = Number(n || 0);
  return "₡" + v.toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtFecha = (iso) => iso ? new Date(`${String(iso).slice(0,10)}T00:00:00`).toLocaleDateString("es-CR") : "";
// Formatea Date → YYYY-MM-DD en huso horario LOCAL (evita el desplazamiento
// que causa .toISOString() para usuarios en UTC-6, ej. Costa Rica).
const toLocalISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function setupPdfFont(doc) {
  doc.addFileToVFS("DejaVuSans.ttf", DEJAVU_REGULAR_BASE64);
  doc.addFont("DejaVuSans.ttf", "DejaVu", "normal");
  doc.addFileToVFS("DejaVuSans-Bold.ttf", DEJAVU_BOLD_BASE64);
  doc.addFont("DejaVuSans-Bold.ttf", "DejaVu", "bold");
  doc.setFont("DejaVu", "normal");
}

function pdfHeader(doc, title, subtitle, empresa) {
  setupPdfFont(doc);
  doc.setFontSize(16); doc.setFont("DejaVu", "bold");
  doc.text(empresa?.razon_social || empresa?.nombre || "Empresa", 14, 16);
  doc.setFontSize(13);
  doc.text(title, 14, 24);
  doc.setFontSize(9); doc.setFont("DejaVu", "normal");
  doc.setTextColor(100);
  if (subtitle) doc.text(subtitle, 14, 30);
  doc.text(`Generado: ${new Date().toLocaleString("es-CR")}`, 14, 35);
  doc.setTextColor(0);
}

function downloadPdf(doc, fileName) { doc.save(fileName); }

function exportExcel(rows, headers, fileName, title) {
  const wb = XLSX.utils.book_new();
  const wsData = [
    [title || ""],
    [],
    headers,
    ...rows,
  ];
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  // estilo: header row (índice 2)
  const headerStyle = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "274735" } }, alignment: { horizontal: "center" } };
  for (let c = 0; c < headers.length; c++) {
    const ref = XLSX.utils.encode_cell({ r: 2, c });
    if (ws[ref]) ws[ref].s = headerStyle;
  }
  // Título en bold grande
  if (ws["A1"]) ws["A1"].s = { font: { bold: true, sz: 14 } };
  // Ancho de columnas auto
  ws["!cols"] = headers.map((h, i) => ({
    wch: Math.max(h.length + 2, ...rows.map(r => String(r[i] ?? "").length + 2))
  }));
  XLSX.utils.book_append_sheet(wb, ws, "Reporte");
  XLSX.writeFile(wb, fileName);
}

// ============================================================================
// EMPLEADOS
// ============================================================================
export function reporteEmpleados({ format, empleados, empresa }) {
  const headers = ["Cédula", "Nombre", "Puesto", "Departamento", "Jornada", "Estado", "Fecha Ingreso", "Salario Base"];
  const rows = empleados.map(e => [
    e.cedula || "",
    `${e.nombre || ""} ${e.apellidos || ""}`.trim(),
    e.puesto || "",
    e.departamento || "",
    e.jornada || "",
    e.estado || "",
    fmtFecha(e.fecha_ingreso),
    Number(e.salario_base) || 0,
  ]);
  const fileBase = `Reporte_Empleados_${new Date().toISOString().slice(0,10)}`;
  if (format === "excel") {
    const rowsTxt = rows.map(r => r.map((v, i) => i === 7 ? colones(v) : v));
    exportExcel(rowsTxt, headers, `${fileBase}.xlsx`, "Reporte de Empleados");
    return;
  }
  const doc = new jsPDF({ orientation: "landscape" });
  pdfHeader(doc, "Reporte de Empleados", `Total: ${empleados.length}`, empresa);
  autoTable(doc, {
    startY: 42, head: [headers],
    body: rows.map(r => r.map((v, i) => i === 7 ? colones(v) : v)),
    styles: { font: "DejaVu", fontSize: 8 },
    headStyles: { fillColor: [39, 71, 53], textColor: 255 },
  });
  downloadPdf(doc, `${fileBase}.pdf`);
}

// ============================================================================
// VACACIONES
// ============================================================================
export function reporteVacaciones({ format, rows, empresa }) {
  // rows: [{empleado, ingreso, antiguedad, generadas, tomadas, saldo, porVencer}]
  const headers = ["Empleado", "Fecha Ingreso", "Antigüedad", "Generadas", "Tomadas", "Saldo", "Por vencer"];
  const tabular = rows.map(r => [
    r.empleado, fmtFecha(r.ingreso), r.antiguedad || "",
    Number(r.generadas) || 0, Number(r.tomadas) || 0,
    Number(r.saldo) || 0, Number(r.porVencer) || 0,
  ]);
  const fileBase = `Reporte_Vacaciones_${new Date().toISOString().slice(0,10)}`;
  if (format === "excel") {
    exportExcel(tabular, headers, `${fileBase}.xlsx`, "Reporte de Vacaciones");
    return;
  }
  const doc = new jsPDF({ orientation: "landscape" });
  pdfHeader(doc, "Reporte de Vacaciones", `Total empleados: ${rows.length}`, empresa);
  autoTable(doc, { startY: 42, head: [headers], body: tabular, styles: { font: "DejaVu", fontSize: 9 }, headStyles: { fillColor: [39, 71, 53], textColor: 255 } });
  downloadPdf(doc, `${fileBase}.pdf`);
}

// ============================================================================
// ASISTENCIA (rango de fechas)
// ============================================================================
export function reporteAsistencia({ format, asistencias, empleados, fechaInicio, fechaFin, empresa }) {
  const empMap = {}; empleados.forEach(e => { empMap[e.id] = `${e.nombre || ""} ${e.apellidos || ""}`.trim(); });
  // Mapa puesto→noHorasExtras (normalizado) para excluir extras de puestos exentos (Art. 143 CT).
  const puestosMap = new Map();
  const norm = (s) => (s == null ? "" : String(s)).trim().toLowerCase();
  ((empresa && Array.isArray(empresa.puestos)) ? empresa.puestos : []).forEach(p => {
    const k = norm(p?.nombre); if (k) puestosMap.set(k, !!p?.no_horas_extras);
  });
  const empPuesto = {}; empleados.forEach(e => { empPuesto[e.id] = e.puesto || ""; });
  const isNoExt = (empId) => puestosMap.get(norm(empPuesto[empId])) === true;

  const headers = ["Fecha", "Empleado", "Entrada", "Salida", "Jornada", "Horas Ord.", "Horas Extra", "Estado"];
  const rows = asistencias.map(a => [
    fmtFecha(a.fecha),
    empMap[a.empleado_id] || "—",
    a.entrada || "",
    a.salida || "",
    a.jornada || "",
    Number(a.horas_ordinarias ?? a.horas ?? 0),
    isNoExt(a.empleado_id) ? 0 : Number(a.horas_extras || 0),
    a.estado || "Presente",
  ]);
  const sub = `Del ${fmtFecha(fechaInicio)} al ${fmtFecha(fechaFin)} · ${rows.length} registros`;
  const fileBase = `Reporte_Asistencia_${fechaInicio}_a_${fechaFin}`;
  if (format === "excel") { exportExcel(rows, headers, `${fileBase}.xlsx`, `Asistencia ${fmtFecha(fechaInicio)} → ${fmtFecha(fechaFin)}`); return; }
  const doc = new jsPDF({ orientation: "landscape" });
  pdfHeader(doc, "Reporte de Asistencia", sub, empresa);
  autoTable(doc, { startY: 42, head: [headers], body: rows, styles: { font: "DejaVu", fontSize: 8 }, headStyles: { fillColor: [39, 71, 53], textColor: 255 } });
  downloadPdf(doc, `${fileBase}.pdf`);
}

// ============================================================================
// INCAPACIDADES
// ============================================================================
export function reporteIncapacidades({ format, items, empleados, periodoLabel, empresa }) {
  const empMap = {}; empleados.forEach(e => { empMap[e.id] = `${e.nombre || ""} ${e.apellidos || ""}`.trim(); });
  const headers = ["Empleado", "Tipo", "Inicio", "Fin", "Días", "Pago Patrono", "Pago CCSS", "Total"];
  const rows = items.map(i => [
    empMap[i.empleado_id] || "—",
    i.tipo || "",
    fmtFecha(i.fecha_inicio), fmtFecha(i.fecha_fin),
    Number(i.dias || 0),
    Number(i.monto_patrono || 0),
    Number(i.monto_ccss || 0),
    (Number(i.monto_patrono || 0) + Number(i.monto_ccss || 0)),
  ]);
  const fileBase = `Reporte_Incapacidades_${periodoLabel.replace(/[^\w-]+/g, "_")}`;
  if (format === "excel") {
    const rowsTxt = rows.map(r => r.map((v, i) => i >= 5 ? colones(v) : v));
    exportExcel(rowsTxt, headers, `${fileBase}.xlsx`, `Incapacidades ${periodoLabel}`); return;
  }
  const doc = new jsPDF({ orientation: "landscape" });
  pdfHeader(doc, "Reporte de Incapacidades", periodoLabel + ` · ${rows.length} registros`, empresa);
  autoTable(doc, { startY: 42, head: [headers], body: rows.map(r => r.map((v, i) => i >= 5 ? colones(v) : v)), styles: { font: "DejaVu", fontSize: 9 }, headStyles: { fillColor: [39, 71, 53], textColor: 255 } });
  downloadPdf(doc, `${fileBase}.pdf`);
}

// ============================================================================
// REBAJOS / BONIFICACIONES (estructura similar)
// ============================================================================
function reporteMontos({ format, items, empleados, periodoLabel, empresa, titulo, fileBase }) {
  const empMap = {}; empleados.forEach(e => { empMap[e.id] = `${e.nombre || ""} ${e.apellidos || ""}`.trim(); });
  const headers = ["Empleado", "Fecha", "Concepto", "Monto"];
  const rows = items.map(r => [
    empMap[r.empleado_id] || "—",
    fmtFecha(r.fecha),
    r.concepto || r.descripcion || "",
    Number(r.monto || 0),
  ]);
  const total = rows.reduce((s, r) => s + r[3], 0);
  const sub = `${periodoLabel} · ${rows.length} registros · Total: ${colones(total)}`;
  if (format === "excel") {
    const rowsTxt = rows.map(r => r.map((v, i) => i === 3 ? colones(v) : v));
    rowsTxt.push(["", "", "Total", colones(total)]);
    exportExcel(rowsTxt, headers, `${fileBase}.xlsx`, `${titulo} ${periodoLabel}`); return;
  }
  const doc = new jsPDF({ orientation: "portrait" });
  pdfHeader(doc, titulo, sub, empresa);
  autoTable(doc, {
    startY: 42, head: [headers],
    body: rows.map(r => r.map((v, i) => i === 3 ? colones(v) : v)),
    foot: [["", "", "Total", colones(total)]],
    styles: { font: "DejaVu", fontSize: 9 },
    headStyles: { fillColor: [39, 71, 53], textColor: 255 },
    footStyles: { fillColor: [230, 239, 234], textColor: 0, fontStyle: "bold" },
  });
  downloadPdf(doc, `${fileBase}.pdf`);
}

export function reporteRebajos(opts) {
  return reporteMontos({ ...opts, titulo: "Reporte de Rebajos", fileBase: `Reporte_Rebajos_${opts.periodoLabel.replace(/[^\w-]+/g, "_")}` });
}
export function reporteBonificaciones(opts) {
  return reporteMontos({ ...opts, titulo: "Reporte de Bonificaciones", fileBase: `Reporte_Bonificaciones_${opts.periodoLabel.replace(/[^\w-]+/g, "_")}` });
}

// ============================================================================
// Helpers: rangos de fechas para período mes/semana/quincena
// ============================================================================
export function rangoSemana(date = new Date()) {
  const d = new Date(date); d.setHours(0, 0, 0, 0);
  const dia = d.getDay(); // 0 (dom) ... 6 (sab)
  const diff = (dia + 6) % 7; // Lunes como inicio
  const start = new Date(d); start.setDate(d.getDate() - diff);
  const end = new Date(start); end.setDate(start.getDate() + 6);
  return { start: toLocalISO(start), end: toLocalISO(end), label: `Semana del ${fmtFecha(toLocalISO(start))} al ${fmtFecha(toLocalISO(end))}` };
}

export function rangoQuincena(date = new Date()) {
  const d = new Date(date); d.setHours(0, 0, 0, 0);
  const first = d.getDate() <= 15;
  const start = new Date(d.getFullYear(), d.getMonth(), first ? 1 : 16);
  const end = new Date(d.getFullYear(), d.getMonth(), first ? 15 : new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate());
  return { start: toLocalISO(start), end: toLocalISO(end), label: `Quincena del ${fmtFecha(toLocalISO(start))} al ${fmtFecha(toLocalISO(end))}` };
}

export function rangoMes(date = new Date()) {
  const d = new Date(date);
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const meses = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Setiembre","Octubre","Noviembre","Diciembre"];
  return { start: toLocalISO(start), end: toLocalISO(end), label: `${meses[d.getMonth()]} ${d.getFullYear()}` };
}
